import { ISapAssetDetail } from "@/types/sapAsset.type"
import { isAssetTemplateV2 } from "./assetColumns"

const toIntegerOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null

  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.trunc(value) : null
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null
  }

  return null
}

const toSplitQuantity = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const buildFallbackGroupKey = (row: ISapAssetDetail, index: number): string =>
  String(row.numbering_group_key || row._id || `${row.record_id || "asset"}-group-${index + 1}`)

const normalizeExistingGroups = (items: ISapAssetDetail[] = []): ISapAssetDetail[] => {
  const seenGroupOrder = new Map<string, number>()
  let nextGroupOrder = 1

  return items.map((row, index) => {
    const groupKey = buildFallbackGroupKey(row, index)
    const requestedGroupOrder = toIntegerOrNull(row.numbering_group_order)

    if (!seenGroupOrder.has(groupKey)) {
      seenGroupOrder.set(groupKey, requestedGroupOrder || nextGroupOrder)
      if (!requestedGroupOrder) {
        nextGroupOrder += 1
      } else {
        nextGroupOrder = Math.max(nextGroupOrder, requestedGroupOrder + 1)
      }
    }

    return {
      ...row,
      numbering_group_key: groupKey,
      numbering_group_order: seenGroupOrder.get(groupKey) || nextGroupOrder,
      numbering_row_order: toIntegerOrNull(row.numbering_row_order) || 1,
    }
  })
}

const buildSplitClone = (
  row: ISapAssetDetail,
  numberingGroupKey: string,
  numberingGroupOrder: number,
  numberingRowOrder: number,
): ISapAssetDetail => {
  const {
    _id: _discardId,
    created_at: _discardCreatedAt,
    updated_at: _discardUpdatedAt,
    created_by: _discardCreatedBy,
    updated_by: _discardUpdatedBy,
    ...rest
  } = row

  return {
    ...rest,
    quantity: 1,
    asset_number: "",
    asset_subnumber: "",
    numbering_group_key: numberingGroupKey,
    numbering_group_order: numberingGroupOrder,
    numbering_row_order: numberingRowOrder,
  }
}

export const expandAssetRowsForNumbering = (
  items: ISapAssetDetail[] = [],
  templateKey?: string | null,
): ISapAssetDetail[] => {
  if (!isAssetTemplateV2(templateKey, items)) {
    return items
  }

  const normalizedRows = normalizeExistingGroups(items)

  return normalizedRows.flatMap((row, index) => {
    const quantity = toSplitQuantity(row.quantity)
    const shouldSplit = Number.isInteger(quantity) && (quantity || 0) > 1
    const numberingGroupKey = String(row.numbering_group_key || buildFallbackGroupKey(row, index))
    const numberingGroupOrder = toIntegerOrNull(row.numbering_group_order) || index + 1

    if (!shouldSplit) {
      return [
        {
          ...row,
          numbering_group_key: numberingGroupKey,
          numbering_group_order: numberingGroupOrder,
          numbering_row_order: toIntegerOrNull(row.numbering_row_order) || 1,
        },
      ]
    }

    const totalRows = Number(quantity)
    const firstRow: ISapAssetDetail = {
      ...row,
      quantity: 1,
      asset_number: row.asset_number || "",
      asset_subnumber: row.asset_subnumber || "",
      numbering_group_key: numberingGroupKey,
      numbering_group_order: numberingGroupOrder,
      numbering_row_order: 1,
    }

    const nextRows = Array.from({ length: totalRows - 1 }, (_, cloneIndex) =>
      buildSplitClone(row, numberingGroupKey, numberingGroupOrder, cloneIndex + 2),
    )

    return [firstRow, ...nextRows]
  })
}

export const getAssetNumberingGroupInfo = (
  item: ISapAssetDetail,
  groupSize: number,
): { title: string; helper: string } => {
  const groupOrder = toIntegerOrNull(item.numbering_group_order) || 1
  return {
    title: `Asset Group ${groupOrder}`,
    helper: `${groupSize} row${groupSize > 1 ? "s" : ""} for numbering`,
  }
}
